import { HttpClient } from '@angular/common/http';
import { Injectable, Provider, inject } from '@angular/core';
import { Observable, map, of, shareReplay, switchMap, timer } from 'rxjs';
import {
  GridApiService, GridColumnInfo, GridCountRequest, GridCountResponse, GridDistinctValuesRequest,
  GridDistinctValuesResponse, GridFilter, GridQueryRequest, GridQueryResponse, GridRow,
} from '@inavista/components/grid';

/** The apiBaseUrl prefix of a grid served by RemoteSearchGridApi. */
export const REMOTE_SEARCH_URL = 'rims-search://';

/** The synthetic row key RemoteSearchGridApi adds to every row (the old endpoints have no unique column). */
export const REMOTE_SEARCH_ROW_KEY = '__rowKey';

/** One `parameters` entry of the old FilterInput body (KeyValueObject in RIMS API). */
export interface RemoteSearchParam {
  key: string;
  value: string;
  oprtr?: string;
}

/** A grid served from an old Syncfusion CustomAdaptor endpoint (POST FilterInput, answers PaginationResponse). */
export interface RemoteSearchSource {
  /** Full URL of the old endpoint, e.g. `${apiUrl}SoldToBP/GetCustomerSearchView`. */
  endpoint: string;
  /** The grid's columns: `[field, dataType]`. Only these fields are ever sent as filter or sort fields. */
  columns: [string, string][];
  /** The fields the old toolbar search sent (`onSearch.fieldNames`): every column with a field, hidden ones too. */
  searchFields: string[];
  /** The `parameters` the old Query carried, after the CustomAdaptor's rewrites. `null` = not searched yet: no rows, no call. */
  parameters: RemoteSearchParam[] | null;
  /**
   * Toolbar settings that the old page applied as a column filter (e.g. Items' On Hand checkbox, which called
   * `filterByColumn`): the setting's `queryParam` → the filter its value stands for, or null for none. The filter
   * goes into `onWhere` with the column filters, as the old grid sent it; the parameter itself is not sent.
   */
  paramFilters?: Record<string, (value: string) => GridFilter | null>;
}

// The grid's operators → the Syncfusion operator names RIMS API's SQLFilters.AddFilterNew reads.
const OPERATORS: Record<string, string> = {
  contains: 'contains', equals: 'equal', notEquals: 'notequal', startsWith: 'startswith', endsWith: 'endswith',
  doesNotContain: 'doesnotcontain', doesNotStartWith: 'doesnotstartwith', doesNotEndWith: 'doesnotendwith',
  empty: 'isempty', greaterThan: 'greaterthan', greaterThanOrEqual: 'greaterthanorequal', lessThan: 'lessthan',
  lessThanOrEqual: 'lessthanorequal',
};

// How many rows the old Excel (checkbox) filter read to list a column's values (Syncfusion filterChoiceCount).
const DISTINCT_TAKE = 1000;

/**
 * A GridApiService for the grids that still read an old Syncfusion CustomAdaptor endpoint (LIBRARY-REQUESTS A1,
 * the host-provided seam approved by Martin 2026-10-28). The endpoint, its request and its response are unchanged:
 * this class builds the very FilterInput body the CustomAdaptor built (`requiresCounts`, `onWhere`, `onSearch`,
 * `onSortBy`, `parameters`, `skip`, `take`) from the grid's query, and reads `result` and
 * `paginationData.totalRecords` back. The page is paged by offset (`skip`), carried in the grid's cursor.
 *
 * Provide it on the host component (`providers: [...provideRemoteSearchGrid()]`), give the grid an `apiBaseUrl`
 * starting with `rims-search://` and `keyColumn: REMOTE_SEARCH_ROW_KEY`, and `set(url, source)` before the grid
 * loads. After a change of `parameters` (the page's own search form), call the grid's `refresh()`.
 * Any other URL (and persistence, which has its own service) goes to the real service.
 */
@Injectable()
export class RemoteSearchGridApi extends GridApiService {
  private readonly http = inject(HttpClient);
  private readonly server = inject(GridApiService, { skipSelf: true });
  private readonly sources = new Map<string, RemoteSearchSource>();
  // The first page of the current filters/search/parameters, so /count reads its total instead of a second request.
  private readonly firstPages = new Map<string, { signature: string; response: Observable<any> }>();

  set(url: string, source: RemoteSearchSource) {
    this.sources.set(url, source);
  }

  private static isRemote(url: string) {
    return url?.startsWith(REMOTE_SEARCH_URL);
  }

  override getSchema(url: string, params?: Record<string, string>): Observable<GridColumnInfo[]> {
    if (!RemoteSearchGridApi.isRemote(url)) return this.server.getSchema(url, params);
    const columns: [string, string][] = [...(this.sources.get(url)?.columns ?? []), [REMOTE_SEARCH_ROW_KEY, 'int']];
    return of(columns.map(([columnName, dataType], i) => ({
      ordinalPosition: i + 1, columnName, dataType, isNullable: true, characterMaxLength: null,
    })));
  }

  override query(url: string, req: GridQueryRequest, params?: Record<string, string>): Observable<GridQueryResponse> {
    if (!RemoteSearchGridApi.isRemote(url)) return this.server.query(url, req, params);
    const source = this.sources.get(url);
    if (!source?.parameters) return of({ rows: [], nextCursor: null, hasMore: false });
    const skip = Number(req.cursor) || 0;
    const filters = this.filters(source, req.filters, params);
    const body = this.body(source, filters, req.search, skip, req.pageSize);
    // Every sort level (Ctrl+click, multiSort), in order, as the Syncfusion adaptor sent them; only the grid's own columns.
    const levels = (req.sorts?.length ? req.sorts : req.sort ? [req.sort] : []).filter(s => source.columns.some(([c]) => c === s.column));
    if (levels.length) {
      body.onSortBy = levels.map(s => ({ fieldName: s.column, direction: s.direction === 'desc' ? 'descending' : 'ascending' }));
    }
    let response = this.http.post<any>(source.endpoint, body);
    if (!skip) {
      response = response.pipe(shareReplay(1));
      this.firstPages.set(url, { signature: this.signature(source, filters, req.search), response });
    }
    return response.pipe(map(res => {
      const rows: GridRow[] = (res?.result ?? []).map((row, i) => ({ ...row, [REMOTE_SEARCH_ROW_KEY]: skip + i }));
      const total = res?.paginationData?.totalRecords ?? 0;
      const hasMore = rows.length > 0 && skip + rows.length < total;
      return { rows, nextCursor: hasMore ? String(skip + rows.length) : null, hasMore };
    }));
  }

  override count(url: string, req: GridCountRequest, params?: Record<string, string>): Observable<GridCountResponse> {
    if (!RemoteSearchGridApi.isRemote(url)) return this.server.count(url, req, params);
    const source = this.sources.get(url);
    if (!source?.parameters) return of({ count: 0, cached: false });
    const filters = this.filters(source, req.filters, params);
    const signature = this.signature(source, filters, req.search);
    // The grid asks for the first page and the count together: wait a tick so the first page is the one counted.
    return timer(0).pipe(switchMap(() => {
      const first = this.firstPages.get(url);
      const response = first?.signature === signature
        ? first.response
        : this.http.post<any>(source.endpoint, this.body(source, filters, req.search, 0, 1));
      return response.pipe(map(res => ({ count: res?.paginationData?.totalRecords ?? 0, cached: false })));
    }));
  }

  // The old Excel filter listed the values of the first rows the endpoint returned (no filter, no search).
  override getDistinctValues(url: string, req: GridDistinctValuesRequest, params?: Record<string, string>): Observable<GridDistinctValuesResponse> {
    if (!RemoteSearchGridApi.isRemote(url)) return this.server.getDistinctValues(url, req, params);
    const source = this.sources.get(url);
    if (!source?.parameters || !source.columns.some(([c]) => c === req.column)) return of({ options: [] });
    return this.http.post<any>(source.endpoint, this.body(source, [], '', 0, DISTINCT_TAKE)).pipe(map(res => {
      const values = [...new Set((res?.result ?? []).map(r => r[req.column]).filter(v => v != null && v !== '').map(String))].sort();
      return { options: (values as string[]).map(v => ({ value: v, label: v })) };
    }));
  }

  /** The grid's column filters, then the ones its toolbar settings stand for (`paramFilters`). */
  private filters(source: RemoteSearchSource, filters: GridFilter[] | undefined, params: Record<string, string> | undefined): GridFilter[] {
    const fromParams = Object.entries(source.paramFilters ?? {})
      .map(([param, toFilter]) => (params?.[param] != null ? toFilter(params[param]) : null))
      .filter((f): f is GridFilter => !!f);
    return [...(filters ?? []), ...fromParams];
  }

  /** The FilterInput body, as Syncfusion's CustomAdaptor.setQueryParams built it. */
  private body(source: RemoteSearchSource, filters: GridFilter[] | undefined, search: string | undefined, skip: number, take: number): any {
    const body: any = { requiresCounts: true };
    const predicates = (filters ?? []).map(f => this.predicate(source, f)).filter(p => !!p);
    if (predicates.length) {
      body.onWhere = { isComplex: true, condition: 'and', ignoreAccent: false, predicates };
    }
    if (search) {
      body.onSearch = {
        fieldNames: source.searchFields, operator: 'contains', oprtr: 'contains', searchKey: search,
        ignoreCase: true, ignoreAccent: false,
      };
    }
    // A fresh copy per call: RIMS API removes entries (serviceDepartment, offlineOrder) from the list it receives.
    body.parameters = (source.parameters ?? []).map(p => ({ ...p }));
    body.skip = skip;
    body.take = take;
    return body;
  }

  private predicate(source: RemoteSearchSource, f: GridFilter): any {
    // Only the grid's own columns: a field name goes into the SQL as [field].
    if (!source.columns.some(([c]) => c === f.column)) return null;
    const leaf = (operator: string, value: string) => ({
      isComplex: false, field: f.column, operator, oprtr: operator, value: value ?? '', ignoreCase: true, ignoreAccent: false,
    });
    const values = f.values ?? [];
    if (f.operator === 'in') {
      if (!values.length) return null;
      return values.length === 1 ? leaf('equal', values[0])
        : { isComplex: true, condition: 'or', ignoreAccent: false, predicates: values.map(v => leaf('equal', v)) };
    }
    if (f.operator === 'between') {
      return {
        isComplex: true, condition: 'and', ignoreAccent: false,
        predicates: [leaf('greaterthanorequal', values[0]), leaf('lessthanorequal', values[1])],
      };
    }
    const operator = OPERATORS[f.operator];
    return operator ? leaf(operator, f.operator === 'empty' ? '' : values[0]) : null;
  }

  private signature(source: RemoteSearchSource, filters: GridFilter[] | undefined, search: string | undefined) {
    return JSON.stringify([source.endpoint, source.parameters, filters ?? [], search ?? '']);
  }
}

/** The providers for a host component: its grids ask for GridApiService and get this RemoteSearchGridApi. */
export function provideRemoteSearchGrid(): Provider[] {
  return [RemoteSearchGridApi, { provide: GridApiService, useExisting: RemoteSearchGridApi }];
}

/** The row as the endpoint sent it, without the synthetic key (what the old grid emitted). */
export function remoteSearchRow(row: GridRow): GridRow {
  const { [REMOTE_SEARCH_ROW_KEY]: _key, ...rest } = row;
  return rest;
}
